import type { RespostaPublica } from "../types";
import type { MensagemNaTela, PlanoDaThread } from "../chamados/thread";

/**
 * A costura da thread NA TELA: aplica o plano do `planejarThread` bolha por bolha, em vez
 * de repintar a conversa (DEV-210).
 *
 * Mora fora do `widget.ts` por um motivo prático: o widget vive num Shadow DOM **fechado**,
 * onde nem o Playwright entra, então a única forma de PROVAR a cirurgia é um teste em jsdom
 * — e para isso ela precisa ser chamável sem instanciar o widget inteiro. O que sobrou lá é
 * o que depende do widget: buscar, marcar como visto, carregar os blobs e decidir a rolagem.
 *
 * Os nomes de classe são os da tela do chamado, de propósito: este módulo é o DOM DAQUELA
 * tela, não um utilitário genérico de lista. Parametrizá-los daria a impressão de que serve
 * para outra coisa.
 */

/** As bolhas que estão na lista, na ordem — a entrada do `planejarThread`. */
export function bolhasNaTela(corpo: Element): MensagemNaTela[] {
  const lista = corpo.querySelector(".ch-thread");
  if (!lista) return [];

  return [...lista.querySelectorAll<HTMLElement>("[data-msg]")].map((el) => ({
    id: el.dataset.msg ?? "",
    editadoEm: el.dataset.editado ?? "",
  }));
}

/**
 * Aplica o plano. `html` monta a bolha (é o `htmlDaMensagem` do widget) e `vazio` é o
 * placeholder de "ainda sem respostas".
 *
 * Devolve as respostas que ENTRARAM ou foram repintadas — é por elas que o chamador busca
 * os binários dos anexos. Devolve vazio quando não havia o que costurar (tela em
 * "carregando"/"erro", que não tem lista nem placeholder).
 *
 * Ordem das operações: remover → trocar → inserir. Remover primeiro é o que garante que
 * uma âncora do plano não seja um nó já condenado.
 */
export function costurarNaTela(
  corpo: Element,
  plano: PlanoDaThread,
  html: (r: RespostaPublica) => string,
  vazio: string,
): RespostaPublica[] {
  let lista = corpo.querySelector(".ch-thread");
  const placeholder = corpo.querySelector("[data-thread-vazia]");
  if (!lista && !placeholder) return [];

  // Primeira resposta: o placeholder VIRA a lista, no lugar dele — trocar em vez de
  // inserir no fim é o que mantém a faixa do report acima e o composer abaixo.
  if (!lista && placeholder) {
    placeholder.insertAdjacentHTML("beforebegin", '<div class="ch-thread"></div>');
    placeholder.remove();
    lista = corpo.querySelector(".ch-thread");
  }
  if (!lista) return [];

  for (const id of plano.remover) lista.querySelector(`[data-msg="${id}"]`)?.remove();
  for (const r of plano.trocar) {
    const bolha = lista.querySelector(`[data-msg="${r.id}"]`);
    if (bolha) bolha.outerHTML = html(r);
  }
  for (const { resposta, antesDe } of plano.anexar) {
    const ancora = antesDe === null ? null : lista.querySelector(`[data-msg="${antesDe}"]`);
    if (ancora) ancora.insertAdjacentHTML("beforebegin", html(resposta));
    else lista.insertAdjacentHTML("beforeend", html(resposta));
  }

  // A conversa pode ter esvaziado (o time recolheu tudo): volta o placeholder, senão sobra
  // um buraco onde havia thread.
  if (lista.querySelector("[data-msg]") === null) lista.outerHTML = vazio;

  return [...plano.anexar.map((e) => e.resposta), ...plano.trocar];
}
